import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { FaEdit, FaPlus, FaTimes } from "react-icons/fa";
import BillToPartyModal from "./BillToPartyModal";
import { fetchBillToParties, fetchBillToParty, taxIdLines } from "../services/billToService";
import "../assets/css/ViewCustomer.css";

const MAX_OPTIONS = 50;

function partyHaystack(p) {
  return [p.Name, p.Contact_Person, p.Email, p.Phone, p.GSTIN, p.PAN, p.Tax_Id, p.Country_Name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function partySubline(p) {
  return [p.Contact_Person, p.Email, taxIdLines(p)[0], p.Country_Name].filter(Boolean).join(" · ");
}

function PartyTags({ party }) {
  return (
    <>
      {party.Is_Default ? <span className="bill-to-tag">default</span> : null}
      {party.Is_Self ? <span className="bill-to-tag bill-to-tag--self">own</span> : null}
      {party.Party_Type === "Individual" ? <span className="bill-to-tag bill-to-tag--person">individual</span> : null}
    </>
  );
}

/**
 * Bill To field. With a selection it shows the party as a card (Change / Edit / Remove); "Change"
 * (or no selection) shows a type-ahead over existing parties (name, contact, email, phone, GSTIN /
 * PAN / tax ID, country) with an inline "New bill to".
 *
 * @param {object} props
 * @param {string|number} [props.clientId] - tenant the picker is for (drives Is_Self / Is_Default ordering)
 * @param {string|number|""} props.value - selected Bill_To_Id
 * @param {(id: string, party: object|null) => void} props.onChange
 * @param {boolean} [props.autoSelectDefault=true] - pick the tenant default when value is empty
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.showPreview=true] - address / contact / tax lines on the selected card
 * @param {number} [props.reloadKey] - bump to refetch
 * @param {boolean} [props.setDefaultOnCreate=false] - new parties also become the tenant default
 * @param {boolean} [props.excludeSelf=false] - hide the tenant's own party (when it is not a Bill To company)
 * @param {boolean} [props.allowClear=true] - show "Remove" on the selected card
 */
function BillToPicker({
  clientId,
  value,
  onChange,
  autoSelectDefault = true,
  disabled = false,
  showPreview = true,
  reloadKey = 0,
  setDefaultOnCreate = false,
  excludeSelf = false,
  allowClear = true
}) {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fallbackParty, setFallbackParty] = useState(null);
  const [modalParty, setModalParty] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [changing, setChanging] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await fetchBillToParties({ clientId, status: "Active" });
      const rows =
        excludeSelf && clientId ? all.filter((p) => String(p.tenant_id ?? "") !== String(clientId)) : all;
      setParties(rows);
      return rows;
    } catch (err) {
      console.error("Failed to load bill to parties:", err);
      setParties([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [clientId, excludeSelf]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    if (!autoSelectDefault || loading || !parties.length) return;
    const current = parties.find((p) => String(p.Bill_To_Id) === String(value ?? ""));
    if (current) return;
    const preferred = parties.find((p) => p.Is_Default) || parties.find((p) => p.Is_Self) || null;
    if (preferred) onChange?.(String(preferred.Bill_To_Id), preferred);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parties, loading, autoSelectDefault, clientId]);

  const listed = useMemo(
    () => parties.find((p) => String(p.Bill_To_Id) === String(value ?? "")) || null,
    [parties, value]
  );

  // A saved value that is not in the active list (e.g. deactivated since) is still shown.
  useEffect(() => {
    if (!value || listed || loading) {
      setFallbackParty(null);
      return undefined;
    }
    let cancelled = false;
    fetchBillToParty(value, clientId)
      .then((p) => {
        if (!cancelled && p && String(p.Bill_To_Id) === String(value)) setFallbackParty(p);
      })
      .catch(() => {
        if (!cancelled) setFallbackParty(null);
      });
    return () => {
      cancelled = true;
    };
  }, [value, listed, loading, clientId]);

  const selected = listed || fallbackParty;
  const searching = !selected || changing;

  const matches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const rows = terms.length ? parties.filter((p) => terms.every((t) => partyHaystack(p).includes(t))) : parties;
    return rows.slice(0, MAX_OPTIONS);
  }, [parties, query]);

  const optionCount = matches.length + 1;

  const closeSearch = useCallback(() => {
    setOpen(false);
    setQuery("");
    setChanging(false);
  }, []);

  useEffect(() => {
    if (!open && !changing) return undefined;
    const onDocDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) closeSearch();
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("touchstart", onDocDown);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("touchstart", onDocDown);
    };
  }, [open, changing, closeSearch]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIndex}"]`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  useEffect(() => {
    if (changing) inputRef.current?.focus();
  }, [changing]);

  const openCreate = (name = "") => {
    const typed = name.trim();
    closeSearch();
    setModalParty(typed ? { Name: typed } : null);
    setShowModal(true);
  };

  const pick = (p) => {
    closeSearch();
    onChange?.(String(p.Bill_To_Id), p);
  };

  const choose = (idx) => {
    if (idx < matches.length) pick(matches[idx]);
    else openCreate(query);
  };

  const startChange = () => {
    setChanging(true);
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIndex((i) => (i + 1) % optionCount);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (open) setActiveIndex((i) => (i - 1 + optionCount) % optionCount);
    } else if (e.key === "Enter") {
      // Never let Enter submit the host form from here.
      e.preventDefault();
      if (open) choose(activeIndex);
      else setOpen(true);
    } else if (e.key === "Escape") {
      if (open || changing) {
        e.preventDefault();
        closeSearch();
      }
    } else if (e.key === "Tab") {
      closeSearch();
    }
  };

  const openEdit = () => {
    if (!selected) return;
    setModalParty(selected);
    setShowModal(true);
  };

  const handleSaved = async (saved) => {
    setShowModal(false);
    const rows = await load();
    const match = rows.find((p) => String(p.Bill_To_Id) === String(saved?.Bill_To_Id)) || saved || null;
    if (match) onChange?.(String(match.Bill_To_Id), match);
  };

  const contactLine = selected
    ? [selected.Contact_Person, selected.Email, selected.Phone ? [selected.Country_Code, selected.Phone].filter(Boolean).join(" ") : ""]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div className="bill-to-picker">
      {!searching ? (
        <div className={`bill-to-selected${disabled ? " is-disabled" : ""}`}>
          <div className="bill-to-selected__body">
            <div className="bill-to-selected__name">
              <span>{selected.Name || "—"}</span>
              <PartyTags party={selected} />
              {selected.Status === "Inactive" ? (
                <span className="bill-to-tag bill-to-tag--inactive">inactive</span>
              ) : null}
            </div>
            {showPreview ? (
              <>
                {contactLine ? <div className="bill-to-selected__line">{contactLine}</div> : null}
                {selected.Address ? <div className="bill-to-selected__line">{selected.Address}</div> : null}
                {taxIdLines(selected).length ? (
                  <div className="bill-to-selected__line">{taxIdLines(selected).join(" · ")}</div>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="bill-to-selected__actions">
            <button
              type="button"
              className="tenant-invoices-action-btn"
              title="Edit bill to details"
              aria-label="Edit bill to details"
              onClick={openEdit}
              disabled={disabled}
            >
              <FaEdit />
            </button>
            {allowClear ? (
              <button
                type="button"
                className="tenant-invoices-action-btn bill-to-selected__remove"
                title="Remove bill to"
                aria-label="Remove bill to"
                onClick={() => onChange?.("", null)}
                disabled={disabled}
              >
                <FaTimes />
              </button>
            ) : null}
            <button type="button" className="bill-to-selected__change" onClick={startChange} disabled={disabled}>
              Change
            </button>
          </div>
        </div>
      ) : (
        <div className="tenant-invoices-issuer-row">
          <div className={`bill-to-combo${open ? " is-open" : ""}`} ref={boxRef}>
            <input
              ref={inputRef}
              type="text"
              className="bill-to-combo__input"
              value={query}
              placeholder={
                loading ? "Loading..." : selected ? `Search to replace ${selected.Name || "bill to"}` : "Search or select bill to"
              }
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onClick={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              disabled={disabled || loading}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label="Bill to"
            />
            {open && !disabled && !loading ? (
              <ul className="bill-to-combo__list" role="listbox" id={listId} ref={listRef}>
                {matches.length === 0 ? (
                  <li className="bill-to-combo__empty">
                    {parties.length ? "No matching bill to." : "No bill to parties yet."}
                  </li>
                ) : null}
                {matches.map((p, idx) => {
                  const sub = partySubline(p);
                  const isSel = selected && String(p.Bill_To_Id) === String(selected.Bill_To_Id);
                  return (
                    <li key={p.Bill_To_Id} role="option" aria-selected={!!isSel} data-idx={idx}>
                      <button
                        type="button"
                        className={`bill-to-combo__option${idx === activeIndex ? " is-active" : ""}${
                          isSel ? " is-selected" : ""
                        }`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pick(p)}
                      >
                        <span className="bill-to-combo__title">
                          {p.Name || "—"}
                          <PartyTags party={p} />
                        </span>
                        {sub ? <span className="bill-to-combo__sub">{sub}</span> : null}
                      </button>
                    </li>
                  );
                })}
                <li role="option" aria-selected={false} data-idx={matches.length}>
                  <button
                    type="button"
                    className={`bill-to-combo__option bill-to-combo__option--new${
                      activeIndex === matches.length ? " is-active" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(matches.length)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => openCreate(query)}
                  >
                    <FaPlus aria-hidden="true" />
                    {query.trim() ? <span>New bill to “{query.trim()}”</span> : <span>New bill to…</span>}
                  </button>
                </li>
              </ul>
            ) : null}
          </div>
          {changing ? (
            <button type="button" className="bill-to-selected__change" onClick={closeSearch}>
              Cancel
            </button>
          ) : (
            <button
              type="button"
              className="tenant-invoices-action-btn"
              title="New bill to"
              aria-label="New bill to"
              onClick={() => openCreate()}
              disabled={disabled}
            >
              <FaPlus />
            </button>
          )}
        </div>
      )}
      {showModal ? (
        <BillToPartyModal
          party={modalParty}
          setDefaultForClientId={setDefaultOnCreate ? clientId : undefined}
          onSaved={handleSaved}
          onClose={() => setShowModal(false)}
        />
      ) : null}
    </div>
  );
}

export default BillToPicker;
